import type { JobSalary } from "@lossdog/job-watchlists";
import { getQuantile } from "@lossdog/job-watchlists";
import { useQueryClient } from "@tanstack/react-query";
import type { ColumnDef, Header, SortingState } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { RecentSearches } from "@/components/dashboard/RecentSearches";
import { SuggestedProfiles } from "@/components/dashboard/SuggestedProfiles";
import { JobSalaryLastPrice } from "@/components/job-salary-ticker/JobSalaryLastPrice";
import { JobSalaryNetChange } from "@/components/job-salary-ticker/JobSalaryNetChange";
import { Autocomplete } from "@/components/ui/inputs/Autocomplete/Autocomplete";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";
import TableCell from "@/components/ui/table/TableCell";
import TableHeaderCell from "@/components/ui/table/TableHeaderCell";
import { VirtualizedTable } from "@/components/ui/table/VirtualizedTable";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { WatchlistDropdown } from "@/components/watchlist/WatchlistDropdown";
import type { BizUserRecentPositionsModels } from "@/data/bizUserRecentPositions/bizUserRecentPositions.models";
import { BizUserRecentPositionsQueries } from "@/data/bizUserRecentPositions/bizUserRecentPositions.queries";
import { useJobSalaries } from "@/hooks/useJobSalaries";

interface SearchSectionProps {
  isHidden?: boolean;
  isFocusedOnly?: boolean;
  searchTerm?: string;
}

export const SearchSection = ({ isHidden, isFocusedOnly, searchTerm }: SearchSectionProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();

  // SearchSection has its own independent selections, always defaults to "All"
  const {
    jobSalaries,
    metroAreas,
    quantiles,
    selectedMetroArea,
    selectedQuantile,
    setSelectedMetroArea,
    setSelectedQuantile,
    isLoading,
  } = useJobSalaries({
    defaultToAll: true, // Always use "All" selections
    autoSelectDefaults: true,
  });

  const [sorting, setSorting] = useState<SortingState>([{ id: "title", desc: false }]);
  const [jobSearchTerm, setJobSearchTerm] = useState<string>("");

  const { mutate: addRecentPosition } = BizUserRecentPositionsQueries.useAddRecentPosition({
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: BizUserRecentPositionsQueries.keys.getRecentPositions() });

      // Snapshot the previous value
      const previousRecentPositions =
        queryClient.getQueryData<BizUserRecentPositionsModels.BizRecentPositionResponseDto>(
          BizUserRecentPositionsQueries.keys.getRecentPositions(),
        );

      // Optimistically update to the new value
      if (previousRecentPositions) {
        const newRecentPositions: BizUserRecentPositionsModels.BizRecentPositionResponseDto = {
          ...previousRecentPositions,
          recentPositions: [
            variables.data.positionCode,
            ...previousRecentPositions.recentPositions.filter((pos) => pos !== variables.data.positionCode),
          ],
          updatedAt: new Date().toISOString(),
        };

        queryClient.setQueryData(BizUserRecentPositionsQueries.keys.getRecentPositions(), newRecentPositions);
      }

      // Return a context object with the snapshotted value
      return { previousRecentPositions };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      const typedContext = context as
        | { previousRecentPositions?: BizUserRecentPositionsModels.BizRecentPositionResponseDto }
        | undefined;
      if (typedContext?.previousRecentPositions) {
        queryClient.setQueryData(
          BizUserRecentPositionsQueries.keys.getRecentPositions(),
          typedContext.previousRecentPositions,
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey: BizUserRecentPositionsQueries.keys.getRecentPositions() });
    },
  });

  // Wrapper functions to handle type conversion for dropdowns
  const handleQuantileChange = (item: { id: string; title: string } | null) => {
    if (item) {
      // Find the full Quantile object from the quantiles array
      const fullQuantile = quantiles?.find((q) => q.id === item.id);
      setSelectedQuantile(fullQuantile || null);
    } else {
      setSelectedQuantile(null);
    }
  };

  const handleMetroAreaChange = (item: { id: string; title: string } | null) => {
    if (item) {
      // Find the full MetroArea object from the metroAreas array
      const fullMetroArea = metroAreas?.find((m) => m.id === item.id);
      setSelectedMetroArea(fullMetroArea || null);
    } else {
      setSelectedMetroArea(null);
    }
  };

  // Filter and search jobs with limit
  const filteredJobs = useMemo(() => {
    if (!jobSalaries) return [];

    let filtered = jobSalaries;

    // Apply search filter
    const searchTermToUse = searchTerm || jobSearchTerm;
    if (searchTermToUse?.trim()) {
      const search = searchTermToUse.toLowerCase();
      filtered = filtered.filter((job) => job.title.toLowerCase().includes(search));
    }

    return filtered;
  }, [jobSalaries, searchTerm, jobSearchTerm]);

  // Sync the search term from props
  useEffect(() => {
    setJobSearchTerm(searchTerm || "");
  }, [searchTerm]);

  const columns = useMemo<ColumnDef<JobSalary>[]>(
    () => [
      {
        accessorKey: "title",
        header: ({ header }) => SearchSectionHeaderCell(header, t("watchlist.filters.position")),
        size: 200,
        cell: (info) => SearchSectionCell(info),
      },
      {
        accessorKey: "salary",
        header: ({ header }) => SearchSectionHeaderCell(header, t("watchlist.filters.salary")),
        size: 140,
        cell: (info) => SearchSectionCellSalary(info),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.salary.value || 0;
          const b = rowB.original.salary.value || 0;
          return a - b;
        },
      },
      {
        accessorKey: "netChange",
        header: ({ header }) => SearchSectionHeaderCell(header, t("watchlist.filters.progress")),
        size: 80,
        cell: (info) => SearchSectionCellNetChange(info),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.netChange.value || 0;
          const b = rowB.original.netChange.value || 0;
          return a - b;
        },
      },
    ],
    [t],
  );

  const handleAddPosition = (rowData: JobSalary) => {
    addRecentPosition({ data: { positionCode: rowData.id } });
    router.push(`/job/${rowData.id}`);
  };

  if (isHidden) return null;

  if (isFocusedOnly) return <RecentSearches />;

  return (
    <div className={cx("flex flex-col gap-1-5", isHidden ? "pointer-events-none opacity-0" : "opacity-100")}>
      <div className="flex flex-col gap-11">
        <div className="flex w-full flex-row gap-2">
          {/* {metroAreas && (
            <WatchlistDropdown
              data={filteredMetroAreas ?? []}
              selectedItem={selectedMetroArea}
              setSelectedItem={handleMetroAreaChange}
              hasSearch
              onSearch={setLocalSearch}
              btnClassName="flex-1 !py-2-5 !px-3 !bg-elevation-surface-2"
            />
          )} */}
          {metroAreas && (
            <Autocomplete
              items={
                metroAreas?.map((filteredMetroArea) => ({
                  id: filteredMetroArea.title,
                  label: filteredMetroArea.title,
                })) || []
              }
              label=""
              onSelectionChange={(item) => {
                const metroArea = metroAreas.find((m) => m.title === item);
                if (metroArea) {
                  handleMetroAreaChange(metroArea);
                }
              }}
              selectedKey={selectedMetroArea?.title}
              defaultSelectedKey={selectedMetroArea?.title}
              inputValue={selectedMetroArea?.title}
              menuTrigger="focus"
              variant="filled"
              placeholder="Select Metro Area"
              inputClassname="w-full flex-1 h-8 border !py-1-5 !px-3 !text-text-inverted-tertiary !text-desktop-body-3 flex-shrink-0 truncate"
              inputIconClassname="!h-3-5 !w-3-5 text-text-inverted-tertiary"
              hideLabels
              comboboxClassName={cx("w-full")}
            />
          )}
          {quantiles && (
            <WatchlistDropdown
              data={quantiles}
              selectedItem={selectedQuantile}
              setSelectedItem={handleQuantileChange}
              selectedItemLabel={
                selectedQuantile && (
                  <Typography
                    size="body-4"
                    className="w-full text-start"
                  >
                    <Typography
                      size="body-4"
                      className="text-text-inverted-secondary"
                      as="span"
                    >
                      Exp:{" "}
                    </Typography>
                    {selectedQuantile?.short}
                  </Typography>
                )
              }
              btnClassName="!w-[130px] max-w-[130px] h-8 !py-1-5 !px-3 !bg-elevation-surface-2 "
              useTriggerWidth
              triggerClassName="!w-auto min-w-[130px]"
            />
          )}
        </div>
        <div className={cx("flex flex-col gap-2", filteredJobs.length > 1 && "min-h-[210px]")}>
          <Typography
            size="label-4"
            variant="prominent-1"
            className="text-text-inverted-secondary uppercase"
          >
            {t("dashboard.jobSalaries.title")}
          </Typography>

          <VirtualizedTable
            data={filteredJobs}
            showAllColumns
            columns={columns}
            sorting={sorting}
            onSortingChange={setSorting}
            isLoading={isLoading}
            manualSorting={false}
            className="w-full"
            onRowDataUsed={(rowData) => rowData.subscribe()}
            onRowClick={(rowData) => handleAddPosition(rowData)}
          />
        </div>
        <SuggestedProfiles
          searchTerm={jobSearchTerm}
          metroArea={selectedMetroArea?.title || ""}
          quantileMin={getQuantile(selectedQuantile, "min") || 0}
          quantileMax={getQuantile(selectedQuantile, "max") || 0}
        />
      </div>
    </div>
  );
};

export const SearchSectionHeaderCell = (header: Header<any, unknown>, label: string) => {
  return (
    <TableHeaderCell
      header={header}
      label={label}
      align="start"
    />
  );
};

export const SearchSectionCell = (info: any) => {
  return (
    <TableCell align="start">
      <Tooltip
        text={info.row.original.title}
        offset={30}
        placement="top left"
      >
        <CustomTooltipTrigger className="relative h-full w-full">
          <Typography
            size="body-3"
            className="w-28 truncate"
            variant="prominent-2"
          >
            {info.row.original.title}
          </Typography>
        </CustomTooltipTrigger>
      </Tooltip>
    </TableCell>
  );
};

export const SearchSectionCellSalary = (info: any) => {
  return (
    <TableCell align="start">
      <Typography
        size="body-3"
        variant="prominent-2"
      >
        <JobSalaryLastPrice jobSalary={info.row.original} />
      </Typography>
    </TableCell>
  );
};

export const SearchSectionCellNetChange = (info: any) => {
  return (
    <TableCell
      className="flex flex-row items-center gap-2"
      align="start"
    >
      <Typography
        size="body-3"
        variant="prominent-2"
      >
        <JobSalaryNetChange jobSalary={info.row.original} />
      </Typography>
    </TableCell>
  );
};
